import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GUIA_TITULO, GUIA_VERSAO, capitulosPara } from '../content/guiaDoSistema';

/**
 * Guia de uso do sistema, em capítulos.
 *
 * Abre sozinho no primeiro acesso (ver `useGuiaDoSistema`) e fica
 * disponível para consulta no botão "?" do canto inferior direito.
 *
 * O conteúdo mora em `src/content/guiaDoSistema.js` — esta tela só o
 * apresenta. Os capítulos são filtrados pelo papel de quem está lendo,
 * então o professor não lê sobre montar grade e o gestor não lê sobre
 * lançar chamada.
 */

/** Um bloco do capítulo. O tipo decide a forma. */
function Bloco({ bloco, darkMode }) {
  const corpo = darkMode ? 'text-slate-300' : 'text-slate-600';
  const forte = darkMode ? 'text-slate-100' : 'text-slate-800';

  switch (bloco.tipo) {
    case 'texto':
      return <p className={`text-sm leading-relaxed mb-4 ${corpo}`}>{bloco.texto}</p>;

    case 'passos':
      return (
        <div className="mb-5">
          {bloco.titulo && (
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {bloco.titulo}
            </h4>
          )}
          <ol className="space-y-2.5">
            {bloco.itens.map((item, i) => (
              <li key={i} className="flex gap-3">
                <span
                  className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-[#13983D] to-[#0974BA] text-white text-xs font-bold flex items-center justify-center mt-0.5"
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <span className={`text-sm leading-relaxed ${corpo}`}>{item}</span>
              </li>
            ))}
          </ol>
        </div>
      );

    case 'legenda':
      return (
        <div className="mb-5">
          {bloco.titulo && (
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {bloco.titulo}
            </h4>
          )}
          <dl className="space-y-3">
            {bloco.itens.map((item, i) => (
              <div
                key={i}
                className={`rounded-lg p-3 ${darkMode ? 'bg-slate-800/60 border border-slate-700/50' : 'bg-slate-50 border border-slate-200'}`}
              >
                <dt className={`text-sm font-semibold mb-1 ${forte}`}>{item.rotulo}</dt>
                <dd className={`text-sm leading-relaxed ${corpo}`}>{item.texto}</dd>
              </div>
            ))}
          </dl>
        </div>
      );

    case 'caminho':
      return (
        <div className="mb-5">
          {bloco.titulo && (
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {bloco.titulo}
            </h4>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            {bloco.passos.map((passo, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span className={darkMode ? 'text-slate-600' : 'text-slate-400'} aria-hidden="true">
                    ›
                  </span>
                )}
                <span
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium ${darkMode
                    ? 'bg-[#0974BA]/20 text-[#4BA3D8] border border-[#0974BA]/30'
                    : 'bg-[#0974BA]/10 text-[#0974BA] border border-[#0974BA]/20'}`}
                >
                  {passo}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      );

    case 'aviso':
      return (
        <div
          className={`rounded-lg p-3 mb-4 flex gap-2.5 ${darkMode
            ? 'bg-[#0974BA]/10 border border-[#0974BA]/25'
            : 'bg-[#0974BA]/5 border border-[#0974BA]/20'}`}
        >
          <span aria-hidden="true">💡</span>
          <p className={`text-sm leading-relaxed ${corpo}`}>{bloco.texto}</p>
        </div>
      );

    case 'atencao':
      return (
        <div
          className={`rounded-lg p-3 mb-4 ${darkMode
            ? 'bg-[#F9B047]/10 border border-[#F9B047]/30'
            : 'bg-[#F9B047]/10 border border-[#F9B047]/35'}`}
        >
          {bloco.titulo && (
            <p className="text-sm font-semibold text-[#B77B18] dark:text-[#F9B047] mb-1">
              {bloco.titulo}
            </p>
          )}
          <p className={`text-sm leading-relaxed ${corpo}`}>{bloco.texto}</p>
        </div>
      );

    default:
      return null;
  }
}

/**
 * @param {boolean}  aberto
 * @param {Function} onFechar    - fechar sem concluir (o guia volta a abrir)
 * @param {Function} onConcluir  - chegou ao fim ou dispensou de propósito
 * @param {Object}   user
 * @param {boolean}  darkMode
 * @param {boolean}  primeiroAcesso - muda o texto dos botões e do rodapé
 */
function GuiaDoSistema({ aberto, onFechar, onConcluir, user, darkMode, primeiroAcesso = false }) {
  const capitulos = useMemo(() => capitulosPara(user), [user]);
  const [indice, setIndice] = useState(0);
  const corpoRef = useRef(null);

  // Cada abertura recomeça do início — quem volta ao guia para tirar uma
  // dúvida não quer cair no meio de onde parou da última vez.
  useEffect(() => {
    if (aberto) setIndice(0);
  }, [aberto]);

  // Trocar de capítulo volta a rolagem ao topo: sem isso, o capítulo novo
  // abre no meio quando o anterior era longo.
  useEffect(() => {
    if (corpoRef.current) corpoRef.current.scrollTop = 0;
  }, [indice]);

  // Esc fecha, setas navegam.
  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e) => {
      if (e.key === 'Escape') onFechar();
      if (e.key === 'ArrowRight') setIndice((i) => Math.min(i + 1, capitulos.length - 1));
      if (e.key === 'ArrowLeft') setIndice((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, capitulos.length, onFechar]);

  if (!aberto || capitulos.length === 0) return null;

  const capitulo = capitulos[indice];
  const ultimo = indice === capitulos.length - 1;

  const painel = darkMode
    ? 'bg-slate-900 border border-slate-700'
    : 'bg-white border border-slate-200';
  const forte = darkMode ? 'text-slate-100' : 'text-slate-800';
  const fraco = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guia-titulo"
    >
      <div className={`w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${painel}`}>
        {/* Cabeçalho */}
        <div className={`px-6 py-4 flex items-start justify-between gap-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="min-w-0">
            <h2 id="guia-titulo" className={`text-lg font-bold ${forte}`}>
              {GUIA_TITULO}
            </h2>
            <p className={`text-xs mt-0.5 ${fraco}`}>
              {primeiroAcesso
                ? 'Leva dois minutos. Depois ele fica no botão “?”, sempre à mão.'
                : `Capítulo ${indice + 1} de ${capitulos.length}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar o guia"
            className={`flex-shrink-0 p-2 rounded-lg transition-colors ${darkMode
              ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
              : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Lista de capítulos — some no celular, onde o corpo já ocupa a tela */}
          <nav
            className={`hidden md:block w-56 flex-shrink-0 overflow-y-auto p-3 border-r ${darkMode ? 'border-slate-700 bg-slate-900/50' : 'border-slate-200 bg-slate-50/50'}`}
            aria-label="Capítulos do guia"
          >
            {capitulos.map((cap, i) => (
              <button
                key={cap.id}
                type="button"
                onClick={() => setIndice(i)}
                aria-current={i === indice ? 'true' : undefined}
                className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 text-sm transition-colors flex items-start gap-2 ${i === indice
                  ? darkMode
                    ? 'bg-[#0974BA]/20 text-[#4BA3D8] font-medium'
                    : 'bg-[#0974BA]/10 text-[#0974BA] font-medium'
                  : darkMode
                    ? 'text-slate-400 hover:bg-slate-800'
                    : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <span aria-hidden="true">{cap.icone}</span>
                <span className="min-w-0">{cap.titulo}</span>
              </button>
            ))}
          </nav>

          {/* Corpo do capítulo */}
          <div ref={corpoRef} className="flex-1 overflow-y-auto p-6 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl" aria-hidden="true">{capitulo.icone}</span>
              <h3 className={`text-xl font-bold ${forte}`}>{capitulo.titulo}</h3>
            </div>
            {capitulo.resumo && (
              <p className={`text-sm mb-5 ${fraco}`}>{capitulo.resumo}</p>
            )}
            {capitulo.blocos.map((bloco, i) => (
              <Bloco key={i} bloco={bloco} darkMode={darkMode} />
            ))}
          </div>
        </div>

        {/* Rodapé: progresso e navegação */}
        <div className={`px-6 py-4 flex items-center justify-between gap-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {capitulos.map((cap, i) => (
              <span
                key={cap.id}
                className={`h-1.5 rounded-full transition-all ${i === indice
                  ? 'w-6 bg-[#13983D]'
                  : i < indice
                    ? 'w-1.5 bg-[#13983D]/50'
                    : darkMode ? 'w-1.5 bg-slate-700' : 'w-1.5 bg-slate-300'}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {indice > 0 && (
              <button
                type="button"
                onClick={() => setIndice((i) => i - 1)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${darkMode
                  ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Anterior
              </button>
            )}
            {ultimo ? (
              <button
                type="button"
                onClick={onConcluir}
                className="px-5 py-2 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25 transition-all"
              >
                {primeiroAcesso ? 'Entendi, vamos começar' : 'Fechar'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIndice((i) => i + 1)}
                className="px-5 py-2 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25 transition-all"
              >
                Próximo
              </button>
            )}
          </div>
        </div>

        {primeiroAcesso && !ultimo && (
          <button
            type="button"
            onClick={onConcluir}
            className={`px-6 pb-4 -mt-2 text-xs underline text-left ${fraco} hover:${darkMode ? 'text-slate-300' : 'text-slate-700'}`}
          >
            Pular o guia — depois ele fica no botão “?”
          </button>
        )}
      </div>
    </div>
  );
}

export { GUIA_VERSAO };
export default GuiaDoSistema;
